import { hasSessionsWriteAccess, useOptionalAuth } from '@/contexts/AuthContext';
import type { ReferralResult, ReferralState } from '@/types/referrals';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { referralRequest } from './api';

export const useReferralState = (taskId: number | undefined, open: boolean) => {
  const auth = useOptionalAuth();
  const client = useQueryClient();
  const key = useMemo(
    () => ['referral', auth?.authAccountId, auth?.authOrgId, taskId] as const,
    [auth?.authAccountId, auth?.authOrgId, taskId]
  );
  const allowed = Boolean(auth?.user && !auth.isImpersonating && !auth.isViewingAsDelegate);
  const query = useQuery({
    queryKey: key,
    enabled: open && Boolean(taskId) && allowed,
    retry: false,
    queryFn: () => referralRequest<ReferralState>(`/api/tasks/${taskId}/referral/draft`),
    staleTime: 0,
  });
  const attemptId = query.data?.attempt?.id;
  const status = useQuery({
    queryKey: [...key, 'attempt', attemptId],
    enabled: open && allowed && Boolean(attemptId),
    retry: false,
    queryFn: () => referralRequest<ReferralResult>(`/api/tasks/${taskId}/referral/attempts/${attemptId}`),
    refetchInterval: (query) =>
      ['confirmed', 'rejected', 'needs_investigation'].includes(query.state.data?.attempt?.state ?? '') ? false : 5000,
  });
  useEffect(() => {
    if (!status.data?.attempt || status.data.attempt.id !== attemptId) return;
    const result = status.data;
    client.setQueryData<ReferralState>(key, (current) => {
      if (
        !current ||
        !result.attempt ||
        (current.attempt?.id === result.attempt.id && current.attempt.version > result.attempt.version)
      )
        return current;
      const hold = result.hold_reason ?? current.hold_reason;
      const archived = Boolean(current.archived || result.archived);
      const editable = !archived && !hold && result.attempt.state === 'rejected';
      if (
        current.attempt?.id === result.attempt.id &&
        current.attempt.version === result.attempt.version &&
        current.attempt.state === result.attempt.state &&
        current.hold_reason === hold &&
        current.editable === editable &&
        Boolean(current.archived) === archived
      )
        return current;
      return { ...current, attempt: result.attempt, hold_reason: hold, editable, archived };
    });
  }, [status.data, attemptId, client, key]);
  const visible = Boolean(query.data?.enabled || query.data?.has_attempt);
  const canWrite = allowed && hasSessionsWriteAccess(auth?.user ?? null);
  return {
    ...query,
    key,
    visible,
    probing: open && allowed && Boolean(taskId) && query.isPending,
    locked: visible && (!query.data?.editable || !query.data?.enabled || !canWrite),
    canWrite,
    featureEnabled: auth?.user?.orgSettings?.heklaReferralsEnabled === true,
  };
};
